import { useState, useEffect, useCallback, useRef } from "react";
import { WifiOff, Wifi } from "lucide-react";

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const wasOfflineRef = useRef(false);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        setJustReconnected(true);
        setTimeout(() => setJustReconnected(false), 4000);
      }
      wasOfflineRef.current = false;
    };
    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline, justReconnected };
}

export function NetworkStatus() {
  const { isOnline, justReconnected } = useNetworkStatus();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setVisible(true);
    } else if (justReconnected) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 4000);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [isOnline, justReconnected]);

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] flex justify-center pointer-events-none">
      <div
        className={`
          flex items-center gap-2 px-4 py-2 rounded-b-lg shadow-lg text-sm font-medium
          transition-all duration-300 pointer-events-auto
          ${!isOnline
            ? "bg-red-600 text-white"
            : "bg-emerald-600 text-white"
          }
        `}
      >
        {!isOnline ? (
          <>
            <WifiOff className="w-4 h-4" />
            <span>You are offline</span>
          </>
        ) : (
          <>
            <Wifi className="w-4 h-4" />
            <span>Back online</span>
          </>
        )}
      </div>
    </div>
  );
}
